import { FormEvent, useEffect, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { X } from 'lucide-react';
import {
  collectSubscriptionPayment,
  type CollectSubscriptionPaymentInput,
  type Subscription,
} from '@/lib/api/subscriptions.js';
import { formatCurrency } from '@/lib/format.js';
import { parseApiError } from '@/lib/api/errors.js';
import { toastError, toastSuccess } from '@/lib/toast.js';

type PaymentMethod = CollectSubscriptionPaymentInput['method'];

export function CollectSubscriptionPaymentModal({
  open,
  subscription,
  onClose,
  onSuccess,
}: {
  open: boolean;
  subscription: Subscription | null;
  onClose: () => void;
  onSuccess: () => void;
}) {
  const [method, setMethod] = useState<PaymentMethod>('cash');
  const [amount, setAmount] = useState('');
  const [cashTendered, setCashTendered] = useState('');
  const [notes, setNotes] = useState('');

  const defaultAmount = subscription ? Number(subscription.amount) : 0;

  useEffect(() => {
    if (open && subscription) {
      setMethod('cash');
      setAmount(String(defaultAmount));
      setCashTendered('');
      setNotes('');
    }
  }, [open, subscription, defaultAmount]);

  const amountValue = Math.max(Number(amount) || 0, 0);
  const cashTenderedValue = Number(cashTendered || amountValue);
  const changeDue = method === 'cash' ? Math.max(cashTenderedValue - amountValue, 0) : 0;

  const mutation = useMutation({
    mutationFn: (body: CollectSubscriptionPaymentInput) => {
      const paymentId = subscription!.pendingPaymentId;
      if (!paymentId) {
        return Promise.reject(new Error('No pending payment found for this subscription'));
      }
      return collectSubscriptionPayment(subscription!.id, paymentId, body);
    },
    onSuccess: (result) => {
      toastSuccess(`Payment of ${formatCurrency(result.payment.amount)} collected`);
      if (result.cashSessionWarning) toastError(result.cashSessionWarning);
      onSuccess();
      onClose();
    },
    onError: (error) => {
      const parsed = parseApiError(error);
      toastError(parsed.message, parsed.code);
    },
  });

  if (!open || !subscription) return null;

  function handleSubmit(event: FormEvent) {
    event.preventDefault();
    if (amountValue <= 0) {
      toastError('Enter a valid payment amount');
      return;
    }
    mutation.mutate({
      amount: amountValue,
      method,
      cashTendered: method === 'cash' ? cashTenderedValue : undefined,
      notes: notes.trim() || undefined,
    });
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <button
        type="button"
        aria-label="Close"
        className="absolute inset-0 bg-slate-950/40"
        onClick={onClose}
      />
      <div className="relative w-full max-w-md rounded-2xl border border-slate-200 bg-white p-6 shadow-xl">
        <div className="mb-4 flex items-start justify-between">
          <div>
            <h2 className="text-lg font-bold text-slate-950">Collect Payment</h2>
            <p className="mt-1 text-sm text-slate-500">
              {subscription.name} · {subscription.customerName ?? 'Customer'}
            </p>
          </div>
          <button type="button" className="rounded-lg p-2 text-slate-500 hover:bg-slate-100" onClick={onClose}>
            <X className="h-5 w-5" />
          </button>
        </div>

        <form className="space-y-4" onSubmit={handleSubmit}>
          <label className="block text-sm font-medium text-slate-700">
            Amount
            <input
              type="number"
              min="0"
              step="0.01"
              className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
            />
          </label>

          <label className="block text-sm font-medium text-slate-700">
            Payment method
            <select
              className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2"
              value={method}
              onChange={(e) => setMethod(e.target.value as PaymentMethod)}
            >
              <option value="cash">Cash</option>
              <option value="card">Card</option>
              <option value="store_credit">Store Credit</option>
              <option value="check">Check</option>
              <option value="other">Other</option>
            </select>
          </label>

          {method === 'cash' ? (
            <label className="block text-sm font-medium text-slate-700">
              Cash tendered
              <input
                type="number"
                min="0"
                step="0.01"
                className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2"
                value={cashTendered}
                onChange={(e) => setCashTendered(e.target.value)}
                placeholder={String(amountValue)}
              />
              {changeDue > 0 ? (
                <p className="mt-1 text-sm text-slate-500">Change due: {formatCurrency(changeDue)}</p>
              ) : null}
            </label>
          ) : null}

          <label className="block text-sm font-medium text-slate-700">
            Notes (optional)
            <textarea
              className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2"
              rows={2}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
            />
          </label>

          <div className="flex gap-2 pt-2">
            <button
              type="button"
              className="flex-1 rounded-xl border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
              onClick={onClose}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={mutation.isPending}
              className="flex-1 rounded-xl bg-primary px-4 py-2 text-sm font-semibold text-white hover:bg-[#164477] disabled:opacity-50"
            >
              {mutation.isPending ? 'Processing…' : 'Collect Payment'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
